import type { OrderCra } from "@ifa/utils";

export type ZuuRiskAnswerMap = Record<string, string>;
export type ZuuRiskOptionLabels = Record<string, string>;
export type ZuuRiskProjectResult = {
  project: string;
  result: string;
  reason?: string | null;
};

export type ZuuRiskValueKind =
  | "plain"
  | "yesNo"
  | "riskLevel"
  | "wealthSource"
  | "transactionCount";

const GENERIC_LABELS: Record<string, string> = {
  A: "是",
  B: "否",
  C: "不適用",
  YES: "是",
  NO: "否",
  TRUE: "是",
  FALSE: "否",
};

const RISK_LABELS: Record<string, string> = {
  LOW: "低風險",
  LOW_RISK: "低風險",
  A: "低風險",
  MIDDLE: "中風險",
  MEDIUM: "中風險",
  MEDIUM_RISK: "中風險",
  MID: "中風險",
  B: "中風險",
  HIGH: "高風險",
  HIGH_RISK: "高風險",
  C: "高風險",
  NO_RISK: "無風險",
};

const WEALTH_SOURCE_LABELS: Record<string, string> = {
  A: "工資",
  B: "儲蓄",
  C: "投資",
  D: "遺產承繼",
  E: "公司利潤",
  F: "其他",
};

const TRANSACTION_COUNT_LABELS: Record<string, string> = {
  A: "少於 10 次",
  B: "10 至 50 次",
  C: "多於 50 次",
};

export function parseZuuRiskAnswers(cra?: Pick<OrderCra, "answer"> | null): ZuuRiskAnswerMap {
  const map: ZuuRiskAnswerMap = {};

  cra?.answer?.forEach((item) => {
    if (!item.question) return;

    const selected = item.items
      ?.filter((option) => option.value === true)
      .map((option) => option.key)
      .filter((option): option is string => Boolean(option));

    if (selected?.length) {
      map[item.question] = selected.join(" / ");
      return;
    }

    const rawValue = item.items?.find(
      (option) => typeof option.value === "string" || typeof option.value === "number",
    )?.value;

    if (typeof rawValue === "string" || typeof rawValue === "number") {
      map[item.question] = String(rawValue);
    }
  });

  return map;
}

export function parseZuuRiskProjectResults(payload: unknown): ZuuRiskProjectResult[] {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown[] } | null)?.data)
      ? (payload as { data: unknown[] }).data
      : Array.isArray((payload as { craResult?: unknown[] } | null)?.craResult)
        ? (payload as { craResult: unknown[] }).craResult
        : [];

  return source
    .filter(
      (item): item is { project: string; result: string; reason?: string | null } =>
        Boolean(
          item &&
            typeof item === "object" &&
            typeof (item as { project?: unknown }).project === "string" &&
            typeof (item as { result?: unknown }).result === "string",
        ),
    )
    .map((item) => ({
      project: item.project,
      result: item.result,
      reason: item.reason,
    }));
}

export function resolveZuuRiskAnswer(
  answers: ZuuRiskAnswerMap,
  aliases: string[],
): { key: string; value: string } | null {
  for (const alias of aliases) {
    const value = answers[alias];
    if (typeof value === "string" && value.trim()) {
      return { key: alias, value };
    }
  }
  return null;
}

function mapTokens(value: string, dictionary: ZuuRiskOptionLabels) {
  return value
    .split("/")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => dictionary[token] ?? dictionary[token.toUpperCase()] ?? token)
    .join(" / ");
}

export function formatZuuRiskValue(
  value: string,
  kind: ZuuRiskValueKind,
  optionLabels?: ZuuRiskOptionLabels,
): string {
  if (optionLabels) {
    return mapTokens(value, optionLabels);
  }

  switch (kind) {
    case "yesNo":
      return mapTokens(value, GENERIC_LABELS);
    case "riskLevel":
      return mapTokens(value, RISK_LABELS);
    case "wealthSource":
      return mapTokens(value, WEALTH_SOURCE_LABELS);
    case "transactionCount":
      return mapTokens(value, TRANSACTION_COUNT_LABELS);
    case "plain":
    default:
      return value;
  }
}
